/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, DoCheck, Input, OnInit, ViewChild} from '@angular/core';
import {CommonModule, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {DisplayPreferencesService} from '../../../common/service/display-preferences.service';
import {MapModeService} from '../../services/map-mode.service';
import {LookupService} from '../../../common/service/lookup.service';
import {WidgetConfigLookup} from '../../../common/models/widget-config.model';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {WidgetService} from '../../services/widget.service';
import {MapToolsService} from '../../services/map-tools.service';
import {AppConfigService} from '../../../common/service/app-config.service';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {HelpLinkComponent} from '../../../help/help-link.component';

@Component({
    selector: 'nlr-display-preferences',
    imports: [CommonModule, FormsModule, CdkDrag, CdkDragHandle, HelpLinkComponent],
    providers: [CommonModule],
    templateUrl: './display-preferences.component.html',
    styleUrl: './display-preferences.component.scss'
})
export class DisplayPreferencesComponent implements OnInit, DoCheck {
    @Input()
    disabled: boolean;

    @ViewChild('widget')
    widgetElement: HTMLElement;

    title = 'DisplayPreferencesController';

    widgetId = 'displayPreferences';

    widgetConfig: WidgetConfigLookup = {};

    showHiddenAnnotations = false;

    showHiddenGeometryFeatures = false;

    showEnlargementPolygons = false;

    showClippingPolygons = false;

    showPrintArea = false;

    mainPlan = true;

    applyDisabled = true;

    viewMode: string;

    widgetVisibleAtLastCheck: boolean;

    helpLinkUrl: string = HELP_LINK_CONTENTS_BASE_URL + 'display-preferences-help.html';

    helpPageTitle: string = 'Manage Feature Display Preferences';

    constructor(
        private destroyRef: DestroyRef,
        private appConfigService: AppConfigService,
        private displayPreferencesService: DisplayPreferencesService,
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private mapModeService: MapModeService,
        private mapToolsService: MapToolsService,
        private widgetService: WidgetService
    ) {}

    ngOnInit() {
        console.debug(`${this.title} - ngOnInit`);

        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find((l: WidgetConfigLookup) => l.name === this.widgetId);

        this.mainPlan = this.mapModeService.isMapModeType('mainPlan');

        this.viewMode = this.mapModeService.viewMode;

        this.initDisplayPreferences();

        this.eventBus
            .on('displayModeChanged')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                const displayMode = meta.data;
                console.debug(`${this.title} - displayModeChanged - ${displayMode}`);
                this.viewMode = displayMode;
                this.displayPreferencesChanged();
            });

        this.eventBus
            .on('mapModeChanged')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - mapModeChanged`);
                this.mainPlan = this.mapModeService.isMapModeType('mainPlan');
            });
    }

    ngDoCheck() {
        this.applyDisabled = this.isApplyDisabled();

        if (this.widgetElement && !this.widgetVisibleAtLastCheck) {
            this.widgetService.setupWidget(this.widgetId);
            this.initDisplayPreferences();
        }
        this.widgetVisibleAtLastCheck = !!this.widgetElement;
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    toggleWidgetVisibility() {
        this.widgetService.onClick('DisplayPreferences');
    }

    isWidgetActive() {
        return this.widgetService.isWidgetActive('DisplayPreferences');
    }

    getTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('DISPLAY_PREFERENCES_WINDOW', 'Manage feature display preferences');
    }

    getDisplayPreferencesHelpUrl() {
        return `/components/help/content/display-preferences-help.html`;
    }

    getDisplayPreferencesHelpPageTitle() {
        return 'Manage Feature Display Preferences';
    }

    displayPreferencesChanged() {
        this.displayPreferencesService.displayPreferencesChanged({
            showAllAnnotationFeatures: this.showHiddenAnnotations,
            showAllGeometryFeatures: this.showHiddenGeometryFeatures,
            showEnlargementFeatures: this.showEnlargementPolygons,
            showClippingFeatures: this.showClippingPolygons,
            showPrintArea: this.showPrintArea
        });

        this.mapModeService.updateMapMode();
    }

    toggleShowHiddenAnnotations() {
        this.showHiddenAnnotations = !this.showHiddenAnnotations;
    }

    toggleShowHiddenGeometryFeatures() {
        this.showHiddenGeometryFeatures = !this.showHiddenGeometryFeatures;
    }

    showEnlargementPolygonsIsDisabled() {
        return this.isPrintPreview() && !this.mainPlan;
    }

    toggleShowEnlargementFeatures() {
        if (this.showEnlargementPolygonsIsDisabled()) {
            return;
        }
        this.showEnlargementPolygons = !this.showEnlargementPolygons;
    }

    showClippingPolygonsIsDisabled() {
        return this.isPrintPreview();
    }

    toggleShowClippingPolygons() {
        if (this.showClippingPolygonsIsDisabled()) {
            return;
        }
        this.showClippingPolygons = !this.showClippingPolygons;
    }

    showPrintAreaIsDisabled() {
        return this.isPrintPreview() || !this.mainPlan;
    }

    toggleShowPrintArea() {
        if (this.showPrintAreaIsDisabled()) {
            return;
        }
        this.showPrintArea = !this.showPrintArea;
    }

    isApplyDisabled() {
        if (this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures !== this.showHiddenAnnotations) {
            return false;
        }

        if (this.displayPreferencesService.displayPreferences.showAllGeometryFeatures !== this.showHiddenGeometryFeatures) {
            return false;
        }

        if (
            !this.showEnlargementPolygonsIsDisabled() &&
            this.displayPreferencesService.displayPreferences.showEnlargementFeatures !== this.showEnlargementPolygons
        ) {
            return false;
        }

        if (
            !this.showClippingPolygonsIsDisabled() &&
            this.displayPreferencesService.displayPreferences.showClippingFeatures !== this.showClippingPolygons
        ) {
            return false;
        }

        if (!this.showPrintAreaIsDisabled() && this.displayPreferencesService.displayPreferences.showPrintArea !== this.showPrintArea) {
            return false;
        }

        return true;
    }

    private isPrintPreview() {
        return this.viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.PRINT_PREVIEW_MODE;
    }

    private initDisplayPreferences() {
        this.showHiddenAnnotations = this.displayPreferencesService.displayPreferences.showAllAnnotationFeatures;
        this.showHiddenGeometryFeatures = this.displayPreferencesService.displayPreferences.showAllGeometryFeatures;
        this.showEnlargementPolygons = this.displayPreferencesService.displayPreferences.showEnlargementFeatures;
        this.showClippingPolygons = this.displayPreferencesService.displayPreferences.showClippingFeatures;
        this.showPrintArea = this.displayPreferencesService.displayPreferences.showPrintArea;
    }
}
